import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from './ui/Button';
import { ToastCard } from './ui/Toast';
import Tooltip from './ui/Tooltip';
import { usePresence } from './ui/motion';
import { IconRefresh } from './ui/icons';

/**
 * The PWA update prompt. Listens for `swUpdateAvailable` (dispatched by
 * serviceWorkerRegistration.js when a new service worker is installed and
 * waiting) and shows a persistent notice at the bottom of the screen.
 *
 * Three rules, each one a decision:
 *
 * - **Nothing reloads under somebody's hands.** Activating the new worker is a
 *   click on THIS tab's button, never automatic. The one exception is the
 *   unattended TV routes (`AUTO_UPDATE_ROUTES`), where nobody is there to click;
 *   those render no prompt at all.
 * - **An update met AT LOAD is left waiting, with no prompt.** The page fetched
 *   its HTML from the network a moment ago (navigations are network-first in
 *   service-worker.js), so it already runs the build the waiting worker carries;
 *   a prompt would offer an update this tab has. It is NOT activated either:
 *   activation clears the old build's chunks from the store, and a tab still on
 *   that build mid-task would lose them without anyone clicking. The worker
 *   waits for a click in one of those tabs, or for the last of them to close.
 *   When it does take over, this tab remembers it as the build it is already on
 *   and stays quiet. The TV routes, unattended, let it through at once.
 * - **Every tab that needs the update shows the prompt.** `updatefound` fires in
 *   every tab holding the registration, and a worker already waiting at load is
 *   announced by the registration code, so each tab hears on its own. When one
 *   tab activates the update, the new worker takes control of ALL tabs
 *   (`clientsClaim`), but only the tab that clicked reloads; the others switch
 *   to "Updated in another tab" and reload when their own button is pressed.
 *   A tab that was never told still sees its controller change, so it shows the
 *   same notice. Until it reloads, its JavaScript is the old build: fine for the
 *   work in hand, and wrong the moment it lazy-loads a chunk it has not visited
 *   (the barcode scanner modal, the 3D flight path, the pilot simulator), whose
 *   old URL is no longer served. `RootErrorBoundary` catches that failure and
 *   says "reload"; this prompt stays up so the choice is there before it does.
 * - **Not dismissable.** A dismissed prompt is a waiting worker nobody will ever
 *   let through, and every reload keeps serving the old build until they do.
 *   It is small, it sits in a corner, and it goes away the moment it is acted on.
 *
 * Drawn as a toast (ui/Toast's card, info tone) that cannot be pushed out by
 * the toast system: it lives independently of ToastContext on purpose.
 */

const AUTO_UPDATE_ROUTES = ['/shop', '/office'];

function isAutoUpdateRoute(): boolean {
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  return AUTO_UPDATE_ROUTES.includes(path);
}

type Phase =
  /** Nothing to say. */
  | 'idle'
  /** A new worker is installed and waiting; this tab may activate it. */
  | 'waiting'
  /** The new worker took over (activated from another tab); this tab is still on old code. */
  | 'activated-elsewhere';

/** What serviceWorkerRegistration.js dispatches on `swUpdateAvailable`. */
export interface UpdateAnnouncement {
  registration: ServiceWorkerRegistration;
  /** The worker was already waiting or installing when this page loaded. */
  atLoad: boolean;
}

export default function UpdatePrompt() {
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);
  const [phase, setPhase] = useState<Phase>('idle');
  // The worker THIS tab asked to take over. controllerchange then means "you
  // asked, here it is": reload. Cleared once used, so a later activation from
  // another tab cannot reuse an old yes.
  const asked = useRef<ServiceWorker | null>(null);
  // The worker met at load: the build this page already runs. Its later
  // takeover, from another tab's click or the TV route's own, is not news here.
  const alreadyOn = useRef<ServiceWorker | null>(null);

  const ask = useCallback((worker: ServiceWorker) => {
    asked.current = worker;
    worker.postMessage({ type: 'SKIP_WAITING' });
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      const { registration: reg, atLoad } = (e as CustomEvent).detail as UpdateAnnouncement;
      if (atLoad && reg?.waiting) {
        alreadyOn.current = reg.waiting;
        if (isAutoUpdateRoute()) reg.waiting.postMessage({ type: 'SKIP_WAITING' });
        return;
      }
      setRegistration(reg);
      setPhase('waiting');
      if (isAutoUpdateRoute() && reg?.waiting) ask(reg.waiting);
    };
    document.addEventListener('swUpdateAvailable', handler);
    return () => document.removeEventListener('swUpdateAvailable', handler);
  }, [ask]);

  // Listen from mount, not from the announcement: a tab that was never told
  // about an update still has to notice when another tab activates one. A
  // change FROM null is the first-ever install claiming the page and means
  // nothing; a change from a real controller is a new build taking over.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const sw = navigator.serviceWorker;
    let hadController = !!sw.controller;
    const onControllerChange = () => {
      const wasControlled = hadController;
      hadController = true;
      if (alreadyOn.current && sw.controller === alreadyOn.current) {
        alreadyOn.current = null;
        return;
      }
      if (asked.current) {
        asked.current = null;
        window.location.reload();
        return;
      }
      if (wasControlled) setPhase('activated-elsewhere');
    };
    sw.addEventListener('controllerchange', onControllerChange);
    return () => sw.removeEventListener('controllerchange', onControllerChange);
  }, []);

  const activate = useCallback(() => {
    // Branch on what the registration actually holds, not on what the prompt
    // last heard. A waiting worker gets the message. No waiting worker and the
    // new one already controls us: reload onto it. No waiting worker and the
    // OLD one still controls us (a newer build started installing and made the
    // announced worker redundant): a reload would land on the old build, so ask
    // for the update instead and let the announcement come back.
    if (registration?.waiting) {
      ask(registration.waiting);
    } else if (phase === 'activated-elsewhere') {
      window.location.reload();
    } else {
      registration?.update();
    }
  }, [registration, phase, ask]);

  const show = phase !== 'idle' && !isAutoUpdateRoute();
  const { mounted, open } = usePresence(show);
  const copy =
    phase === 'activated-elsewhere'
      ? { text: 'Updated in another tab. Reload when you are ready.', button: 'Reload' }
      : { text: 'New version available', button: 'Update' };

  // The live region is always in the tree, empty until there is news: a region
  // inserted with its text already in it is often never announced. The card
  // inside takes no role of its own, so this stays the one status region.
  return (
    <div
      role="status"
      className={mounted ? 'pointer-events-none fixed inset-x-0 bottom-4 z-[55] flex justify-center px-4' : 'sr-only'}
    >
      {mounted && (
        <ToastCard
          tone="info"
          from="bottom"
          open={open}
          icon={<IconRefresh />}
          className="w-80 max-sm:w-full"
          action={(
            <Tooltip label="Reload this tab onto the new version">
              <Button variant="tonal" size="sm" onClick={activate}>{copy.button}</Button>
            </Tooltip>
          )}
        >
          {copy.text}
        </ToastCard>
      )}
    </div>
  );
}
